import { Link } from "react-router-dom";
import { useI18n } from "../i18n";

const HomeHighlights = () => {
  const { homeHighlights } = useI18n();

  return (
    <section className="relative isolate overflow-hidden  px-6 py-20 text-slate-900 md:px-12 md:py-28">
      {/* Abstract background */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
      >
        {/* Main gradient glows */}
        <div className="absolute -right-40 -top-48 h-[35rem] w-[35rem] rounded-full bg-sky-200/40 blur-[120px]" />

        <div className="absolute -bottom-48 -left-40 h-[32rem] w-[32rem] rounded-full bg-blue-200/30 blur-[120px]" />

        <div className="absolute left-[35%] top-[25%] h-72 w-72 rounded-full bg-cyan-100/50 blur-[100px]" />

        {/* Abstract flowing shapes */}
        <svg
          className="absolute inset-0 h-full w-full opacity-40"
          viewBox="0 0 1440 900"
          preserveAspectRatio="xMidYMid slice"
          fill="none"
        >
          <defs>
            <linearGradient
              id="abstractLineGradient"
              x1="0"
              y1="0"
              x2="1440"
              y2="900"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#38BDF8" stopOpacity="0" />
              <stop offset="0.5" stopColor="#38BDF8" stopOpacity="0.45" />
              <stop offset="1" stopColor="#6366F1" stopOpacity="0.05" />
            </linearGradient>

            <linearGradient
              id="abstractShapeGradient"
              x1="250"
              y1="100"
              x2="1200"
              y2="800"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#BAE6FD" stopOpacity="0.25" />
              <stop offset="1" stopColor="#C7D2FE" stopOpacity="0.05" />
            </linearGradient>

            <pattern
              id="abstractDots"
              width="30"
              height="30"
              patternUnits="userSpaceOnUse"
            >
              <circle cx="1" cy="1" r="1" fill="#64748B" fillOpacity="0.22" />
            </pattern>
          </defs>

          {/* Fine dot pattern */}
          <rect width="1440" height="900" fill="url(#abstractDots)" />

          {/* Large flowing curves */}
          <path
            d="M-200 650C100 500 180 850 480 650S850 350 1100 500 1400 700 1650 400"
            stroke="url(#abstractLineGradient)"
            strokeWidth="1.5"
          />

          <path
            d="M-200 700C100 550 180 900 480 700S850 400 1100 550 1400 750 1650 450"
            stroke="url(#abstractLineGradient)"
            strokeWidth="1"
          />

          <path
            d="M-150 150C150 300 350 50 600 200S950 400 1200 150 1500 100 1650 250"
            stroke="url(#abstractLineGradient)"
            strokeWidth="1.5"
          />

          <path
            d="M-150 190C150 340 350 90 600 240S950 440 1200 190 1500 140 1650 290"
            stroke="url(#abstractLineGradient)"
            strokeWidth="1"
          />

          {/* Translucent geometric shapes */}
          <path
            d="M950 -100L1450 100L1300 450L800 250Z"
            fill="url(#abstractShapeGradient)"
          />

          <path
            d="M-100 650L250 450L450 800L100 1000Z"
            fill="url(#abstractShapeGradient)"
          />

          {/* Small decorative circles */}
          <circle
            cx="1180"
            cy="220"
            r="100"
            stroke="#38BDF8"
            strokeOpacity="0.25"
          />

          <circle
            cx="1180"
            cy="220"
            r="125"
            stroke="#38BDF8"
            strokeOpacity="0.15"
          />

          <circle
            cx="270"
            cy="690"
            r="85"
            stroke="#6366F1"
            strokeOpacity="0.18"
          />

          <circle
            cx="270"
            cy="690"
            r="110"
            stroke="#6366F1"
            strokeOpacity="0.10"
          />
        </svg>

        {/* Subtle highlight in the center */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-white/40" />
      </div>

      <div className="relative mx-auto max-w-7xl">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-end lg:gap-20">
          {/* Left content */}
          <div className="rounded-2xl
                  border border-white/80 bg-white/80 p-5
                  shadow-sm backdrop-blur-md">
            <p className="mb-5 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.24em] text-sky-700 sm:text-sm">
              <span className="h-px w-8 bg-sky-600" />
              {homeHighlights.eyebrow}
            </p>

            <h2 className="max-w-xl text-3xl font-extrabold leading-tight tracking-tight text-slate-900 sm:text-4xl md:text-5xl">
              {homeHighlights.title}
            </h2>

            <p className="mt-6 max-w-xl text-base leading-7 text-slate-600 sm:text-lg sm:leading-8">
              {homeHighlights.intro}
            </p>

            {/* CTA button */}
            <Link
              to="/services"
              className="
                group mt-8 inline-flex items-center gap-3
                rounded-full bg-sky-600 px-6 py-3.5
                font-semibold text-white
                shadow-lg shadow-sky-600/20
                transition-all duration-300
                hover:-translate-y-1 hover:bg-sky-700
                hover:shadow-xl hover:shadow-sky-600/25
                focus-visible:outline-none
                focus-visible:ring-2 focus-visible:ring-sky-500
                focus-visible:ring-offset-2 focus-visible:ring-offset-slate-50
              "
            >
              {homeHighlights.cta}

              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"
              >
                <path
                  d="M5 12h14m-6-6 6 6-6 6"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
          </div>

          {/* Right cards */}
          <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
            {homeHighlights.items.map((item, index) => (
              <article
                key={item.title}
                className="
                  group relative overflow-hidden rounded-2xl
                  border border-white/80 bg-white/80 p-5
                  shadow-sm backdrop-blur-md
                  transition-all duration-300
                  hover:-translate-y-1 hover:border-sky-200
                  hover:bg-white hover:shadow-xl
                  hover:shadow-slate-300/40
                  sm:p-6
                  lg:flex lg:items-start lg:gap-5
                "
              >
                {/* Hover accent */}
                <div
                  aria-hidden="true"
                  className="
                    absolute left-0 top-0 h-full w-1
                    origin-top scale-y-0 bg-sky-500
                    transition-transform duration-300
                    group-hover:scale-y-100
                  "
                />

                {/* Number */}
                <span
                  className="
                    mb-4 inline-flex h-11 w-11 shrink-0
                    items-center justify-center rounded-xl
                    border border-sky-100 bg-sky-50
                    text-sm font-bold text-sky-700
                    transition-all duration-300
                    group-hover:border-sky-200
                    group-hover:bg-sky-100
                    lg:mb-0
                  "
                >
                  {String(index + 1).padStart(2, "0")}
                </span>

                {/* Text */}
                <div>
                  <h3 className="text-lg font-bold text-slate-900 transition-colors duration-300 group-hover:text-sky-700 sm:text-xl">
                    {item.title}
                  </h3>

                  <p className="mt-2 text-sm leading-6 text-slate-600 sm:text-base">
                    {item.info}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeHighlights;